import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
    OrderApprovalConfig,
    OrderQueueOptions,
    OrderRecipientChange
} from '@trade-platform/ui-shared';
import { ENVIRONMENT, IEnvironment } from '@trade-platform/ui-utils';
import { concatMap, Observable } from 'rxjs';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';
import { ifgGatewayIntegrationFileDecoder, orderQueueOptionsDecoder } from './decoder';

export interface AdminPushUpdatedFormToOrderBody {
    orderId: string;
    formId: string;
}

@Injectable()
export class OrderAdminService {
    private http = inject(HttpClient);
    private environment = inject<IEnvironment>(ENVIRONMENT);

    /** Inserted by Angular inject() migration for backwards compatibility */
    constructor(...args: unknown[]);

    constructor() {}

    convertOrderNigoToNeedsClarification(orderId: string): Observable<any> {
        return this.http.post<null>(
            `${this.environment.adminApi}orders/${orderId}/nigoToNeedsClarification`,
            {}
        );
    }

    convertOrderToWetsign(orderId: string): Observable<any> {
        return this.http.post<null>(
            `${this.environment.adminApi}orders/${orderId}/convertToWetsign`,
            {}
        );
    }

    adminMarkOrderComplete(orderId: string): Observable<any> {
        return this.http.post<null>(
            `${this.environment.adminApi}orders/${orderId}/markComplete`,
            {}
        );
    }

    adminResubmitOrder(orderId: string): Observable<any> {
        return this.http.post<null>(`${this.environment.adminApi}orders/${orderId}/resubmit`, {});
    }

    adminPushUpdatedFormToOrder(orderId: string, formId: string): Observable<any> {
        return this.http.patch<null>(
            `${this.environment.adminApi}orders/${orderId}/forms/${formId}`,
            {}
        );
    }

    /**
     * Uploads a replacement PDF for one form on the order.
     *
     * Keyed on the stable `formId`, never a file id — a form file's id is regenerated every time
     * it is superseded, so an id held by the UI is often already stale.
     *
     * Content-Type is deliberately left unset so the browser supplies the multipart boundary.
     */
    adminReplaceFormOnOrder(orderId: string, formId: string, file: File): Observable<any> {
        const body = new FormData();
        body.append('file', file);

        return this.http.post<null>(
            `${this.environment.adminApi}orders/${orderId}/forms/${formId}/replace`,
            body
        );
    }

    adminMoveOrderBackInWorkflow(orderId: string): Observable<any> {
        return this.http.post<null>(
            `${this.environment.adminApi}orders/${orderId}/moveBackInWorkflow`,
            {}
        );
    }

    adminMoveOrderForwardInWorkflow(orderId: string): Observable<any> {
        return this.http.post<null>(
            `${this.environment.adminApi}orders/${orderId}/moveForwardInWorkflow`,
            {}
        );
    }

    adminAddBlindCarbonCopyToEnvelope(orderId: string): Observable<any> {
        return this.http.put<null>(
            `${this.environment.adminApi}orders/${orderId}/addBlindCarbonCopyToEnvelope`,
            {}
        );
    }

    adminUpdateProductProfileData(orderId: string): Observable<any> {
        return this.http.patch<null>(
            `${this.environment.adminApi}orders/${orderId}/updateProductProfileData`,
            {}
        );
    }
    adminLoadIfgGatewayFiles(orderId: string) {
        return this.http
            .get<null>(`${this.environment.adminApi}orders/${orderId}/getGatewayFiles`, {})
            .pipe(concatMap(p => fromPromise(ifgGatewayIntegrationFileDecoder.decodeToPromise(p))));
    }

    adminResubmitFileToIfgGateway(orderId: string, fileTransmissionId: string) {
        return this.http
            .patch<null>(
                `${this.environment.adminApi}orders/${orderId}/reSendGatewayFile/${fileTransmissionId}`,
                {}
            )
            .pipe(concatMap(p => fromPromise(ifgGatewayIntegrationFileDecoder.decodeToPromise(p))));
    }

    adminRegenerateFileList(orderId: string): Observable<any> {
        return this.http.post(
            `${this.environment.adminApi}orders/${orderId}/generateGatewayFileList`,
            null,
            { responseType: 'text' }
        );
    }

    /**
     * The signer queues this order's firm defines, for the "Move Order Queue" card.
     *
     * Not the same list as the order's applicable approval configs: those are filtered by the rules
     * engine, which by construction drops every queue the order is NOT in — i.e. all the ones this
     * card exists to offer.
     */
    adminLoadOrderQueueOptions(orderId: string): Observable<OrderQueueOptions> {
        return this.http
            .get<unknown>(`${this.environment.adminApi}orders/${orderId}/queueOptions`)
            .pipe(concatMap(p => fromPromise(orderQueueOptionsDecoder.decodeToPromise(p))));
    }

    /**
     * Requested by `configId` rather than by branch name: the server re-derives the branch from the
     * config it is given, so the UI cannot write a value the target config's own rule will not match.
     */
    adminMoveOrderQueue(orderId: string, configId: number): Observable<any> {
        return this.http.post<null>(`${this.environment.adminApi}orders/${orderId}/moveQueue`, {
            configId
        });
    }

    /**
     * Edits or removes one e-sign recipient (trade-platform#10428).
     *
     * `originalEmail` is always sent even though the route treats it as optional: the server
     * resolves the change against the recipient holding that address, and without it a removal
     * lands on whichever recipient happens to match the role name first.
     */
    adminUpdateEnvelopeRecipients(
        orderId: string,
        change: OrderRecipientChange
    ): Observable<unknown> {
        return this.http.post<null>(
            `${this.environment.adminApi}orders/${orderId}/updateEnvelopeRecipients`,
            change
        );
    }

    adminResubmitOrderToIfgGateway(
        orderId: string,
        integrationType: string,
        approvalConfig?: OrderApprovalConfig
    ): Observable<any> {
        return this.http.post<null>(
            `${this.environment.adminApi}orders/${orderId}/resendIntegration`,
            { type: integrationType, action: 'sendOrder', approvalConfig }
        );
    }
}
